import { useState, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, Loader2, Plus } from 'lucide-react';
import { Button } from '@/app/components/ui/button';
import { Input } from '@/app/components/ui/input';
import { Label } from '@/app/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/app/components/ui/card';
import { Textarea } from '@/app/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/app/components/ui/select';
import { useCreatePlatformGearModel, usePlatformManufacturers } from '@/hooks/use-platform';

const ASSET_CATEGORIES = [
  { value: 'carabiner', label: 'Carabiner' },
  { value: 'harness', label: 'Harness' },
  { value: 'rope', label: 'Rope' },
  { value: 'lanyard', label: 'Lanyard' },
  { value: 'sling', label: 'Sling' },
  { value: 'descender', label: 'Descender' },
  { value: 'ascender', label: 'Ascender' },
  { value: 'pulley', label: 'Pulley' },
  { value: 'helmet', label: 'Helmet' },
  { value: 'anchor', label: 'Anchor' },
  { value: 'anchor_strap', label: 'Anchor Strap' },
  { value: 'connector', label: 'Connector' },
  { value: 'fall_arrester', label: 'Fall Arrester' },
  { value: 'fall_arrest_accessory', label: 'Fall Arrest Accessory' },
  { value: 'mobile_fall_arrester', label: 'Mobile Fall Arrester' },
  { value: 'rigging_plate', label: 'Rigging Plate' },
  { value: 'rope_clamp', label: 'Rope Clamp' },
  { value: 'swivel', label: 'Swivel' },
  { value: 'other', label: 'Other' },
];

export function CreateGearModelPage() {
  const navigate = useNavigate();
  const { data: manufacturers = [] } = usePlatformManufacturers();
  const createMutation = useCreatePlatformGearModel();

  const [name, setName] = useState('');
  const [manufacturer, setManufacturer] = useState('');
  const [assetCategory, setAssetCategory] = useState('');
  const [modelNumber, setModelNumber] = useState('');
  const [description, setDescription] = useState('');
  const [serviceLifeYears, setServiceLifeYears] = useState('');
  const [inspectionIntervalDays, setInspectionIntervalDays] = useState('');
  const [productUrl, setProductUrl] = useState('');

  const canSubmit = name.trim() && manufacturer && assetCategory;

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      if (!canSubmit) return;

      const result = await createMutation.mutateAsync({
        name: name.trim(),
        manufacturer: parseInt(manufacturer, 10),
        asset_category: assetCategory,
        model_number: modelNumber.trim() || undefined,
        description: description.trim() || undefined,
        service_life_years: serviceLifeYears ? parseInt(serviceLifeYears, 10) : undefined,
        default_inspection_interval_days: inspectionIntervalDays
          ? parseInt(inspectionIntervalDays, 10)
          : undefined,
        product_url: productUrl.trim() || undefined,
      });

      navigate(`/platform/gear-models/${result.id}`);
    },
    [
      canSubmit,
      name,
      manufacturer,
      assetCategory,
      modelNumber,
      description,
      serviceLifeYears,
      inspectionIntervalDays,
      productUrl,
      createMutation,
      navigate,
    ]
  );

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="sm" asChild>
          <Link to="/platform/gear-models">
            <ArrowLeft className="h-4 w-4 mr-1" />
            Back
          </Link>
        </Button>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Add Gear Model</h1>
          <p className="text-gray-600 text-sm">
            Create a new gear model. Inspection steps will be auto-populated from category defaults.
          </p>
        </div>
      </div>

      <form onSubmit={handleSubmit}>
        <Card>
          <CardHeader>
            <CardTitle>Gear Model Information</CardTitle>
            <CardDescription>
              Provide the basic details for the new gear model. Fields marked with * are required.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label htmlFor="name">Name *</Label>
                <Input
                  id="name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="e.g., ASAP Lock"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="manufacturer">Manufacturer *</Label>
                <Select value={manufacturer} onValueChange={setManufacturer}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select manufacturer" />
                  </SelectTrigger>
                  <SelectContent>
                    {manufacturers.map((m) => (
                      <SelectItem key={m.id} value={String(m.id)}>
                        {m.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="asset_category">Category *</Label>
                <Select value={assetCategory} onValueChange={setAssetCategory}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select category" />
                  </SelectTrigger>
                  <SelectContent>
                    {ASSET_CATEGORIES.map((cat) => (
                      <SelectItem key={cat.value} value={cat.value}>
                        {cat.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="model_number">Model Number</Label>
                <Input
                  id="model_number"
                  value={modelNumber}
                  onChange={(e) => setModelNumber(e.target.value)}
                  placeholder="e.g., B071BA"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="service_life_years">Service Life (years)</Label>
                <Input
                  id="service_life_years"
                  type="number"
                  min="1"
                  value={serviceLifeYears}
                  onChange={(e) => setServiceLifeYears(e.target.value)}
                  placeholder="e.g., 10"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="inspection_interval">Inspection Interval (days)</Label>
                <Input
                  id="inspection_interval"
                  type="number"
                  min="1"
                  value={inspectionIntervalDays}
                  onChange={(e) => setInspectionIntervalDays(e.target.value)}
                  placeholder="e.g., 180"
                />
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="description">Description</Label>
                <Textarea
                  id="description"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Brief description of the gear model..."
                  rows={3}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="product_url">Product URL</Label>
                <Input
                  id="product_url"
                  type="url"
                  value={productUrl}
                  onChange={(e) => setProductUrl(e.target.value)}
                  placeholder="https://..."
                />
              </div>
            </div>

            {createMutation.isError && (
              <div className="p-3 rounded-lg bg-red-50 border border-red-200 text-sm text-red-700">
                Failed to create gear model. Please check the form and try again.
              </div>
            )}

            <div className="flex justify-end gap-3 pt-4 border-t">
              <Button type="button" variant="outline" asChild>
                <Link to="/platform/gear-models">Cancel</Link>
              </Button>
              <Button type="submit" disabled={createMutation.isPending}>
                {createMutation.isPending ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Plus className="h-4 w-4 mr-2" />
                )}
                Create Gear Model
              </Button>
            </div>
          </CardContent>
        </Card>
      </form>
    </div>
  );
}
